import {calculateConstructionCells} from './calculateConstructionCells';
import {checkIfRepairRecord, rewindConstructionState} from './rewindConstructionState';
import {calculateCitiesCache} from '../calculateCitiesCache';
import {checkIfOwner} from '../checkIfOwner';
import {validatePendingBuildings} from '../validatePendingBuildings';
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectHexes,
  selectPendingBuildings,
  selectPlayers,
  selectSpecialBuildings,
} from '../../../redux/reducers/sharedStateReducer/selectors';

// Почему действие снято: размещено некорректно само (по проверке конца хода на
// исходном поле); держалось на снятом — без него его нельзя было сделать или
// оно перестало быть корректным; либо просто стояло в конце хода — когда
// бюджета хватило только на откат с конца и зависимости никто не проверял
export const CONSTRUCTION_ROLLBACK_REASONS = {
  INVALID: 'invalid',
  DEPENDENT: 'dependent',
  TAIL: 'tail',
};

// Почему выборочного плана нет и нужен откат с конца
export const CONSTRUCTION_ROLLBACK_FALLBACK_REASONS = {
  // Хвост хода длиннее, чем планировщик берется считать
  TOO_LONG: 'too_long',
  // Две постройки с одной ячейкой и жетоном: редьюсер отмены снимет обе разом
  DUPLICATE_RECORDS: 'duplicate_records',
  // Расчет не уложился в бюджет вызовов
  BUDGET: 'budget',
  // Некорректно зафиксированное действие (разведка рудника гномов фиксирует
  // все сделанное до нее): снятием более поздних действий поле не исправить
  COMMITTED_INVALID: 'committed_invalid',
};

// Реальный ход — 2–9 действий; бюджет нужен на случай хода, собранного в обход
// правил клика (редьюсеры спецстроений и ремонта число действий не ограничивают).
// Расчет идет синхронно в серверном таймере хода. Замер на поле радиуса 20:
// ход из трех действий — единицы миллисекунд; ход из 9–12 действий, из которых
// некорректны или зависимы все (перебор упирается в бюджет), — 40–70 мс;
// 24 действия — ~120 мс
export const CONSTRUCTION_ROLLBACK_LIMITS = {
  maxTailLength: 24,
  // Длиннее — без перебора наборов: их число растет слишком быстро
  maxExhaustiveTailLength: 12,
  maxCellChecks: 400,
  maxValidations: 400,
};

const PLAN_NAMES = {
  SINGLE_CAUSE: 'single_cause',
  CHEAPEST: 'cheapest',
  LATEST: 'latest',
  FROM_END: 'from_end',
  SMALLEST: 'smallest',
};

class RollbackBudgetExceededError extends Error {}

const EMPTY_INDEXES = new Set();

const sortAscending = (indexes) => [...indexes].sort((a, b) => a - b);

const checkIfSameIndexes = (a, b) => a.size === b.size && [...a].every((index) => b.has(index));

// Какой из двух наборов снятых лучше: тот, что меньше; при равном размере —
// тот, что сохраняет более раннее действие (в первом действии хода, которым
// наборы различаются, лучший его не снимает)
const checkIfBetterRemoval = (candidate, current) => {
  if (candidate.size !== current.size) {
    return candidate.size < current.size;
  }
  const firstDifferentIndex = sortAscending(new Set([...candidate, ...current]))
    .find((index) => candidate.has(index) !== current.has(index));
  return firstDifferentIndex !== undefined && current.has(firstDifferentIndex);
};

/**
 * Модель хода строительства для планировщика: чистые вопросы «что будет, если
 * этих действий не делать» с запоминанием ответов и счетчиком вызовов.
 */
const createRollbackModel = (state, limits) => {
  const pendingBuildings = selectPendingBuildings(state);
  const total = pendingBuildings.length;
  const currentPlayer = selectCurrentPlayer(state);
  const players = selectPlayers(state);
  // Кэш городов стора за ход строительства не меняется — отмена его не трогает
  const citiesCache = selectCitiesCache(state);
  const stats = {cellChecks: 0, validations: 0};
  const clickChecks = new Map();
  const validations = new Map();
  // Действия, о которых по перемотке судить нельзя (см. checkIfDependent)
  const unverifiableIndexes = new Set();

  /**
   * Правило клика для действия index на поле, каким оно было ПЕРЕД ним, без
   * снятых removedIndexes: постройка — доступность и красная блокировка ячейки
   * (calculateConstructionCells), ремонт — принадлежность строения городу
   * игрока. Ответ зависит только от снятых РАНЬШЕ index: все более поздние
   * перематываются всегда
   */
  const checkClick = (index, removedIndexes) => {
    const earlierRemoved = sortAscending(removedIndexes).filter((removedIndex) => removedIndex < index);
    const key = `${index}|${earlierRemoved.join(',')}`;
    const known = clickChecks.get(key);
    if (known) {
      return known;
    }
    stats.cellChecks++;
    if (stats.cellChecks > limits.maxCellChecks) {
      throw new RollbackBudgetExceededError();
    }

    const rewoundIndexes = new Set(earlierRemoved);
    for (let laterIndex = index; laterIndex < total; laterIndex++) {
      rewoundIndexes.add(laterIndex);
    }
    const building = pendingBuildings[index];
    let result;
    if (checkIfRepairRecord(building)) {
      // Чинить можно только строение своего города, а дотянуть город до
      // строения мог как раз снятый квартал
      const rewoundState = rewindConstructionState(state, rewoundIndexes);
      const rewoundCitiesCache = calculateCitiesCache(selectHexes(rewoundState), selectSpecialBuildings(rewoundState));
      result = {
        isAvailable: checkIfOwner(building.hexKey, currentPlayer.color, rewoundCitiesCache),
        isBlocked: false,
      };
    } else {
      const {availableCells, blockedCells} = calculateConstructionCells(
        rewindConstructionState(state, rewoundIndexes, {selectedToken: building.token}),
        {targetHexKey: building.hexKey}
      );
      result = {
        isAvailable: availableCells.includes(building.hexKey),
        isBlocked: blockedCells.has(building.hexKey),
      };
    }
    clickChecks.set(key, result);
    return result;
  };

  /**
   * Действие нельзя было сделать без снятых: с ними оно проходило правило
   * клика, а без них — нет. Сверка именно с исходным ходом, а не «проходит ли
   * сейчас»: действие, которое правило клика не проходило и в исходном ходе
   * (у Людей политика сменилась посреди хода, ход собран в обход клиента),
   * зависимым не считается — судить о нем по перемотке нельзя
   */
  const checkIfDependent = (index, removedIndexes) => {
    const hasEarlierRemoved = [...removedIndexes].some((removedIndex) => removedIndex < index);
    if (!hasEarlierRemoved) {
      return false;
    }
    const original = checkClick(index, EMPTY_INDEXES);
    if (!original.isAvailable) {
      unverifiableIndexes.add(index);
      return false;
    }
    const current = checkClick(index, removedIndexes);
    return !current.isAvailable || (!original.isBlocked && current.isBlocked);
  };

  /**
   * Набор снятых, дополненный всем, что без них не сделать. Один проход по
   * порядку хода: правило клика зависит только от более ранних действий, так
   * что к моменту проверки действия набор более ранних снятых уже окончателен.
   *
   * Действия одной ячейки лежат друг на друге (спецстроение на квартале этого
   * хода, строение на дороге этого хода, ремонты): более позднее снимается
   * вслед за более ранним всегда — редьюсеры отмены умеют снимать ячейку
   * только сверху вниз
   */
  const cascade = (removedIndexes, firstIndex) => {
    const closed = new Set(removedIndexes);
    const removedHexKeys = new Set();
    for (let index = 0; index < total; index++) {
      const hexKey = pendingBuildings[index].hexKey;
      if (!closed.has(index) && index >= firstIndex
        && (removedHexKeys.has(hexKey) || checkIfDependent(index, closed))) {
        closed.add(index);
      }
      if (closed.has(index)) {
        removedHexKeys.add(hexKey);
      }
    }
    return closed;
  };

  /**
   * Проверка конца хода на поле без снятых — те же аргументы, что у
   * validateCurrentPlayerPendingBuildings. Записи помечаются исходным индексом:
   * проверка возвращает их копиями
   */
  const validate = (removedIndexes) => {
    const key = sortAscending(removedIndexes).join(',');
    const known = validations.get(key);
    if (known) {
      return known;
    }
    stats.validations++;
    if (stats.validations > limits.maxValidations) {
      throw new RollbackBudgetExceededError();
    }

    const rewoundState = rewindConstructionState(state, removedIndexes);
    const taggedBuildings = pendingBuildings
      .map((building, planIndex) => ({...building, planIndex}))
      .filter((building) => !removedIndexes.has(building.planIndex));
    const {invalidBuildings} = validatePendingBuildings(
      taggedBuildings,
      selectHexes(rewoundState),
      currentPlayer,
      players,
      citiesCache,
      selectBuildingsDamage(rewoundState),
      selectSpecialBuildings(rewoundState)
    );
    const result = {
      invalidIndexes: new Set(invalidBuildings.map((building) => building.planIndex)),
      errorsByIndex: new Map(invalidBuildings.map((building) => [building.planIndex, building.validationErrors])),
    };
    validations.set(key, result);
    return result;
  };

  return {pendingBuildings, total, stats, cascade, validate, unverifiableIndexes};
};

/**
 * Что снять, чтобы поле стало корректным: виток «снять одно некорректное
 * действие вместе со всем, что без него не сделать, и проверить заново».
 * Какое именно из некорректных снимать, решает chooseVictim.
 */
const buildPlan = (model, firstTailIndex, chooseVictim) => {
  let removedIndexes = new Set();
  for (; ;) {
    const {invalidIndexes} = model.validate(removedIndexes);
    if (invalidIndexes.size === 0) {
      return {removedIndexes, isValid: true};
    }
    // Снятого на поле нет, поэтому некорректным оно оказаться не может
    const candidates = sortAscending(invalidIndexes).filter((index) => index >= firstTailIndex);
    if (candidates.length > 0) {
      removedIndexes = chooseVictim(removedIndexes, candidates);
      continue;
    }
    // Некорректно только зафиксированное: мешать ему может более позднее
    // действие — снимаем хвост с конца
    let lastKeptIndex = model.total - 1;
    while (lastKeptIndex >= firstTailIndex && removedIndexes.has(lastKeptIndex)) {
      lastKeptIndex--;
    }
    if (lastKeptIndex < firstTailIndex) {
      return {removedIndexes, isValid: false};
    }
    removedIndexes = model.cascade(new Set([...removedIndexes, lastKeptIndex]), firstTailIndex);
  }
};

/**
 * План выборочной отмены действий строительства текущего игрока, когда ход
 * закрывает не он сам, а проверка конца хода (validatePendingBuildings) нашла
 * некорректные строения. Чистая функция: стор не трогает.
 *
 * Снимаются только некорректные действия и те, что без них нельзя было
 * сделать. Зависимостей два вида, и проверяются они по-разному:
 *  - «можно ли было кликнуть» (путь строительства, красные ячейки, свой ли
 *    город у ремонта) зависит только от БОЛЕЕ РАННИХ действий хода — дорога за
 *    казармой без квартала проверку конца хода проходит (дороги она не смотрит
 *    вовсе), но без казармы ее не поставить;
 *  - «корректно ли в конце хода» считается по всему оставшемуся набору, в том
 *    числе по более поздним действиям — ферму штатно ставят раньше квартала.
 *
 * Когда некорректных несколько, снимается то, с которым уходит меньше всего:
 * цена действия — сколько снимется вместе с ним плюс сколько некорректных после
 * этого останется (осиротевший базовый квартал, а не центральный в двух ячейках
 * от него, корректный без этого квартала). При равной цене остается
 * построенное раньше. На той же модели считаются еще три плана — «одно
 * некорректное действие, без которого корректно все остальное», «снимать самое
 * позднее некорректное» и откат с конца, как раньше, — и берется тот, что
 * сохраняет больше действий, а при равенстве — более ранние: хуже отката с
 * конца результат не бывает. Затем каждое снятое действие (и то, что держалось
 * только на нем) пробуется вернуть. Все это — жадные шаги, наименьшего набора
 * они не обещают, поэтому на ходе не длиннее maxExhaustiveTailLength напоследок
 * перебираются наборы меньше найденного: там результат — наименьший возможный.
 *
 * Зафиксированные действия (isCommited — все сделанное до разведки рудника
 * гномами и сама разведка) не снимаются: исход броска уже раскрыт. Если
 * некорректно само зафиксированное, плана нет — fallbackReason.
 *
 * Гарантии результата без fallbackReason: поле без снятых проходит проверку
 * конца хода; каждое оставшееся действие, проходившее правило клика в исходном
 * ходе, проходит его и без снятых; действия одной ячейки сняты сверху вниз.
 *
 * @returns {{
 *   removedIndexes: number[],
 *   reasonByIndex: Object<number, string>,
 *   errorsByIndex: Object<number, string[]>,
 *   keptUnverifiableIndexes: number[],
 *   fallbackReason: string|null,
 *   planName: string|null,
 *   stats: {cellChecks: number, validations: number},
 * }} removedIndexes — индексы в pendingBuildings по убыванию (порядок отмены)
 */
export const planConstructionRollback = (state, {limits = CONSTRUCTION_ROLLBACK_LIMITS} = {}) => {
  const model = createRollbackModel(state, limits);
  const {pendingBuildings, total, stats} = model;
  // isExamined: false — снятое не разбиралось (откат с конца без возврата), и
  // про действие, корректное в исходном ходе, известно лишь, что оно стояло
  // позже некорректного
  const createResult = (removedIndexes, {fallbackReason = null, planName = null, isExamined = true} = {}) => {
    const original = fallbackReason ? null : model.validate(EMPTY_INDEXES);
    const removedDescending = sortAscending(removedIndexes).reverse();
    const reasonByIndex = {};
    const errorsByIndex = {};
    removedDescending.forEach((index) => {
      if (original.invalidIndexes.has(index)) {
        reasonByIndex[index] = CONSTRUCTION_ROLLBACK_REASONS.INVALID;
        errorsByIndex[index] = original.errorsByIndex.get(index);
      } else {
        reasonByIndex[index] = isExamined ? CONSTRUCTION_ROLLBACK_REASONS.DEPENDENT : CONSTRUCTION_ROLLBACK_REASONS.TAIL;
      }
    });
    // Оставшиеся действия, о которых нельзя судить, держались ли они на
    // снятых раньше них (см. checkIfDependent)
    const keptUnverifiableIndexes = sortAscending(model.unverifiableIndexes).filter((index) => !removedIndexes.has(index)
      && removedDescending.some((removedIndex) => removedIndex < index));
    return {
      removedIndexes: removedDescending,
      reasonByIndex,
      errorsByIndex,
      keptUnverifiableIndexes,
      fallbackReason,
      planName,
      stats,
    };
  };
  const createFallback = (fallbackReason) => createResult(EMPTY_INDEXES, {fallbackReason});

  const firstTailIndex = pendingBuildings.findLastIndex((building) => building.isCommited) + 1;
  if (total - firstTailIndex > limits.maxTailLength) {
    return createFallback(CONSTRUCTION_ROLLBACK_FALLBACK_REASONS.TOO_LONG);
  }
  const constructionKeys = pendingBuildings
    .filter((building) => !checkIfRepairRecord(building))
    .map((building) => `${building.hexKey}|${building.token}`);
  if (new Set(constructionKeys).size !== constructionKeys.length) {
    return createFallback(CONSTRUCTION_ROLLBACK_FALLBACK_REASONS.DUPLICATE_RECORDS);
  }

  const removeLatest = (removedIndexes, candidates) => model.cascade(
    new Set([...removedIndexes, candidates[candidates.length - 1]]), firstTailIndex
  );
  const removeCheapest = (removedIndexes, candidates) => {
    if (candidates.length === 1) {
      return removeLatest(removedIndexes, candidates);
    }
    let cheapest = null;
    // С конца, строгое сравнение: при равной цене снимается более позднее
    for (let position = candidates.length - 1; position >= 0; position--) {
      const withVictim = model.cascade(new Set([...removedIndexes, candidates[position]]), firstTailIndex);
      const cost = withVictim.size - removedIndexes.size + model.validate(withVictim).invalidIndexes.size;
      if (!cheapest || cost < cheapest.cost) {
        cheapest = {removedIndexes: withVictim, cost};
      }
    }
    return cheapest.removedIndexes;
  };
  // Откат с конца, как до выборочной отмены: набор «все после индекса»
  // замкнут сам по себе — снятых раньше оставшегося действия в нем нет
  const removeLast = (removedIndexes) => {
    let lastKeptIndex = total - 1;
    while (removedIndexes.has(lastKeptIndex)) {
      lastKeptIndex--;
    }
    return new Set([...removedIndexes, lastKeptIndex]);
  };

  // Одно некорректное действие, без которого (и без всего, что на нем
  // держится) корректен весь остальной ход. Витки выше оценивают потери на шаг
  // вперед и на таком ходе ошибаются: ферма, осиротевший квартал с дорогой за
  // ним и центральный квартал у фермы в двух ячейках от осиротевшего — цена
  // квартала и центрального равна, снимается более поздний центральный, а за
  // ним осыпается весь ход, хотя хватило бы квартала с его дорогой
  const buildSingleCausePlan = () => {
    let best = null;
    sortAscending(model.validate(EMPTY_INDEXES).invalidIndexes)
      .filter((index) => index >= firstTailIndex)
      .forEach((index) => {
        const withCause = model.cascade(new Set([index]), firstTailIndex);
        if ((!best || checkIfBetterRemoval(withCause, best)) && model.validate(withCause).invalidIndexes.size === 0) {
          best = withCause;
        }
      });
    return {removedIndexes: best || EMPTY_INDEXES, isValid: Boolean(best)};
  };

  const buildWithinBudget = (planName, build) => {
    try {
      return {...build(), planName};
    } catch (error) {
      if (error instanceof RollbackBudgetExceededError) {
        return null;
      }
      throw error;
    }
  };
  const buildPlanWithinBudget = (planName, chooseVictim) => buildWithinBudget(
    planName, () => buildPlan(model, firstTailIndex, chooseVictim)
  );

  // Считаются от дешевого к дорогому — бюджет общий, и план, на который его
  // не хватило, не должен оставить без более простого. Откат с конца правил
  // клика не трогает вовсе; без него сравнивать не с чем
  const fromEndPlan = buildPlanWithinBudget(PLAN_NAMES.FROM_END, removeLast);
  if (!fromEndPlan) {
    return createFallback(CONSTRUCTION_ROLLBACK_FALLBACK_REASONS.BUDGET);
  }
  const singleCausePlan = buildWithinBudget(PLAN_NAMES.SINGLE_CAUSE, buildSingleCausePlan);
  const latestPlan = buildPlanWithinBudget(PLAN_NAMES.LATEST, removeLatest);
  const cheapestPlan = buildPlanWithinBudget(PLAN_NAMES.CHEAPEST, removeCheapest);
  // Порядок — по предпочтению среди одинаковых наборов
  const plans = [cheapestPlan, singleCausePlan, latestPlan, fromEndPlan].filter((plan) => plan && plan.isValid);
  if (plans.length === 0) {
    return createFallback(CONSTRUCTION_ROLLBACK_FALLBACK_REASONS.COMMITTED_INVALID);
  }
  const bestPlan = plans.reduce(
    (best, plan) => (checkIfBetterRemoval(plan.removedIndexes, best.removedIndexes) ? plan : best)
  );

  // Возврат: снятое действие возвращается, если без него в наборе снятых поле
  // по-прежнему корректно и никто не повисает. Сначала одно, затем вместе со
  // всем, что держится только на нем (ферма, дорога через нее и квартал у фермы
  // корректны только втроем). Каждый возврат строго уменьшает набор снятых
  let removedIndexes = bestPlan.removedIndexes;
  let planName = bestPlan.planName;
  let isReturnFinished = false;
  // Проверка конца хода — первой: она дешевле правил клика и отсеивает почти
  // все наборы
  const checkIfAcceptable = (candidate) => model.validate(candidate).invalidIndexes.size === 0
    && checkIfSameIndexes(model.cascade(candidate, firstTailIndex), candidate);
  const findReturn = () => {
    for (const index of sortAscending(removedIndexes)) {
      const withoutOne = new Set(removedIndexes);
      withoutOne.delete(index);
      if (checkIfAcceptable(withoutOne)) {
        return withoutOne;
      }
      const group = model.cascade(new Set([index]), firstTailIndex);
      const withoutGroup = new Set([...removedIndexes].filter((removedIndex) => !group.has(removedIndex)));
      if (withoutGroup.size < withoutOne.size && checkIfAcceptable(withoutGroup)) {
        return withoutGroup;
      }
    }
    return null;
  };
  // Перебор: нет ли набора меньше найденного. Планы выше жадные и на ходе с
  // несколькими причинами могут снять лишнее целой группой, которую возврат по
  // одному действию не восстановит (ферма и центральный квартал у нее держатся
  // друг на друге: она корректна только с ним, он достижим только через нее).
  // Наборы перебираются от меньших к большим, поэтому первый найденный размер —
  // наименьший возможный; среди наборов этого размера берется сохраняющий
  // более ранние действия. На длинном ходе перебор не запускается, а кончился
  // бюджет — остается лучшее из найденного к этому моменту
  const tailIndexes = pendingBuildings.map((building, index) => index).slice(firstTailIndex);
  const search = {smallest: null};
  // Все наборы из size действий хвоста: набор дополняется действиями хвоста,
  // начиная с позиции position
  const searchRemovalsOfSize = (size, position = 0, combination = []) => {
    if (combination.length === size) {
      const candidate = new Set(combination);
      if ((!search.smallest || checkIfBetterRemoval(candidate, search.smallest)) && checkIfAcceptable(candidate)) {
        search.smallest = candidate;
      }
      return;
    }
    for (let next = position; next <= tailIndexes.length - (size - combination.length); next++) {
      searchRemovalsOfSize(size, next + 1, [...combination, tailIndexes[next]]);
    }
  };
  const searchSmallerRemoval = () => {
    for (let size = 1; size < removedIndexes.size && !search.smallest; size++) {
      searchRemovalsOfSize(size);
    }
  };
  try {
    for (let returned = findReturn(); returned; returned = findReturn()) {
      removedIndexes = returned;
    }
    isReturnFinished = true;
    if (total - firstTailIndex <= limits.maxExhaustiveTailLength) {
      searchSmallerRemoval();
    }
  } catch (error) {
    // Бюджет кончился посреди возврата или перебора: найденный набор снятых
    // уже корректен, остается как есть
    if (!(error instanceof RollbackBudgetExceededError)) {
      throw error;
    }
  }
  if (search.smallest) {
    removedIndexes = search.smallest;
    planName = PLAN_NAMES.SMALLEST;
  }

  return createResult(removedIndexes, {
    planName,
    isExamined: planName !== PLAN_NAMES.FROM_END || isReturnFinished,
  });
};
